import React, { useEffect, useState } from 'react'
import { Link } from 'gatsby'
import { Box, Typography } from '@mui/material'

import { SectionDivider } from '../SectionDivider'
import VideoObjectSchema from '../VideoObjectSchema'

import { rudderslabTrackOnYoutubeVideoPlayback } from '~/utils/common'

type CompetitorCenterContentProps = {
  data: {
    title: string
    subtitle: string
    section_category: string
    description: string
    center_cta: {
      btntext: string
      btnlink: string
      cmnexternallink: string
      cmnlinktext: string
    }
    video_url: string
    show_divider: boolean
  }
  createdAt: string
}

export const CompetitorCenterContent = ({
  data,
  createdAt
}: CompetitorCenterContentProps) => {
  const [videoId, setVideoId] = useState<string | null>(null)

  useEffect(() => {
    import('@justinribeiro/lite-youtube')

    const youtubeVideo = data.video_url?.split('?')[0]?.split('/')
    const playBackId = youtubeVideo
      ? youtubeVideo[youtubeVideo.length - 1]
      : null

    setVideoId(playBackId)
  }, [data.video_url])

  const onPlay = () =>
    rudderslabTrackOnYoutubeVideoPlayback(data.title, videoId)

  return (
    <Box>
      {videoId && (
        <VideoObjectSchema
          name={data.title}
          description={data.description}
          uploadDate={createdAt}
          embedUrl={data.video_url}
        />
      )}

      <Box
        sx={{
          maxWidth: 1152,
          mx: 'auto',
          pb: 12,
          pt: { xs: 15, mobile: 14, laptop: 23 },
          px: { xs: 3, desktop: 0 },
          textAlign: 'center'
        }}
      >
        {data.section_category && (
          <Typography
            sx={{
              fontSize: { xs: 18, tablet: 24 },
              mt: { xs: 1, tablet: 2 }
            }}
          >
            {data.section_category.toUpperCase()}
          </Typography>
        )}

        <Typography variant='h1' sx={{ fontSize: { xs: 24, laptop: 54 } }}>
          {data.title}
        </Typography>

        <Typography
          sx={{ fontSize: { xs: 18, laptop: 24 }, mt: 3, mx: { laptop: 8 } }}
        >
          {data.description}
        </Typography>

        {data.subtitle && (
          <Typography sx={{ fontSize: 18, fontWeight: 'bold', mt: 2 }}>
            {data.subtitle}
          </Typography>
        )}

        {(data.center_cta?.btntext || data.center_cta?.cmnlinktext) && (
          <Box
            component={data.center_cta.btntext ? Link : 'a'}
            to={
              data.center_cta.btntext
                ? data.center_cta.btnlink
                : data.center_cta.cmnexternallink
            }
            sx={{
              background:
                'linear-gradient(135.55deg,#3e35d1 6.09%,#86f8e0 116.03%)',
              borderRadius: 2,
              color: 'text.primary',
              display: 'inline-block',
              fontSize: 14,
              fontWeight: 'bold',
              mt: 3,
              px: { xs: 3, laptop: 5 },
              py: 2
            }}
          >
            {data.center_cta.btntext || data.center_cta.cmnlinktext}
          </Box>
        )}

        {videoId && (
          <Box
            onClick={onPlay}
            sx={{
              mt: 12,
              mx: { mobile: 3 },
              'lite-youtube': {
                height: 430,
                border: 'none',
                borderRadius: 0,
                mx: 'auto'
              }
            }}
          >
            <lite-youtube videoid={videoId} params='rel=0' />
          </Box>
        )}
      </Box>

      {data.show_divider && <SectionDivider />}
    </Box>
  )
}
